/**
 * Format a number to a specific number of decimal places
 * @param {number|string} value - The value to format
 * @param {number} decimals - Number of decimal places
 * @returns {string} Formatted number string
 */
export function math_formatter(value, decimals) {
    // Handle null, undefined, empty string, or non-numeric values
    if (value === null || value === undefined || value === '' || isNaN(parseFloat(value))) {
        return '0';
    }
    
    // Ensure decimals is a valid non-negative integer
    const dec = Math.max(0, parseInt(decimals) || 0);
    
    try {
        const numValue = Number(value);
        
        // Handle Infinity and NaN
        if (!isFinite(numValue)) {
            return '0';
        }
        
        // Use toFixed with one extra decimal for rounding, then truncate
        const fixed = numValue.toFixed(dec + 1);

        // When no decimals are requested, return just the integer part.
        // (A regex with \d{0,0} would otherwise keep a bare trailing dot, e.g. "28.")
        if (dec === 0) {
            const intMatch = fixed.match(/^-?\d+/);
            return intMatch ? intMatch[0] : numValue.toFixed(0);
        }

        const regex = new RegExp('^-?\\d+(?:\\.\\d{0,' + dec + '})?');
        const match = fixed.match(regex);

        if (match && match[0]) {
            return match[0];
        }
        
        // Fallback: just use toFixed directly
        return numValue.toFixed(dec);
    } catch (e) {
        return '0';
    }
};

/**
 * Calculate a percentage of a value
 * @param {number|string} value - The base value
 * @param {number|string} percentage - The percentage to calculate
 * @returns {number} The calculated percentage
 */
export function math_percentage(value, percentage) {
    return (parseFloat(value) / 100) * parseFloat(percentage);
};

/**
 * Calculate what percentage one number is of another
 * @param {number|string} num - The part
 * @param {number|string} num2 - The whole
 * @returns {string} The percentage with 2 decimal places
 */
export function math_percentage_of_number(num, num2) {
    if (!num2 || parseFloat(num2) === 0) return '0.00';
    return ((100 * num) / num2).toFixed(2);
};

/**
 * Divide two numbers safely (handles division by zero)
 * @param {number|string} value - The dividend
 * @param {number|string} divisor - The divisor
 * @returns {number} The result of division
 */
export function math_divide(value, divisor) {
    if (!divisor || parseFloat(divisor) === 0) return 0;
    return parseFloat(value) / parseFloat(divisor);
};

/**
 * Add two numbers with high precision
 * @param {number|string} a - First number
 * @param {number|string} b - Second number
 * @returns {number} The sum
 */
export function math_sum(a, b) {
    return parseFloat(a || 0) + parseFloat(b || 0);
};

/**
 * Subtract two numbers with high precision
 * @param {number|string} a - First number (minuend)
 * @param {number|string} b - Second number (subtrahend)
 * @returns {number} The difference (a - b)
 */
export function math_sub(a, b) {
    return parseFloat(a || 0) - parseFloat(b || 0);
};

/**
 * Multiply two numbers
 * @param {number|string} a - First number
 * @param {number|string} b - Second number
 * @returns {number} The product
 */
export function math_multiply(a, b) {
    return parseFloat(a || 0) * parseFloat(b || 0);
};

/**
 * Compare two numbers for equality with tolerance for floating point
 * @param {number|string} a - First number
 * @param {number|string} b - Second number
 * @param {number} tolerance - Tolerance for comparison (default: 0.00000001)
 * @returns {boolean} Whether the numbers are equal within tolerance
 */
export function math_equals(a, b, tolerance = 0.00000001) {
    return Math.abs(parseFloat(a || 0) - parseFloat(b || 0)) < tolerance;
};

/**
 * Check if a number is greater than another
 * @param {number|string} a - First number
 * @param {number|string} b - Second number
 * @returns {boolean} Whether a > b
 */
export function math_gt(a, b) {
    return parseFloat(a || 0) > parseFloat(b || 0);
};

/**
 * Check if a number is less than another
 * @param {number|string} a - First number
 * @param {number|string} b - Second number
 * @returns {boolean} Whether a < b
 */
export function math_lt(a, b) {
    return parseFloat(a || 0) < parseFloat(b || 0);
};

/**
 * Check if a number is greater than or equal to another
 * @param {number|string} a - First number
 * @param {number|string} b - Second number
 * @returns {boolean} Whether a >= b
 */
export function math_gte(a, b) {
    return parseFloat(a || 0) >= parseFloat(b || 0);
};

/**
 * Check if a number is less than or equal to another
 * @param {number|string} a - First number
 * @param {number|string} b - Second number
 * @returns {boolean} Whether a <= b
 */
export function math_lte(a, b) {
    return parseFloat(a || 0) <= parseFloat(b || 0);
};

/**
 * Get the minimum of two numbers
 * @param {number|string} a - First number
 * @param {number|string} b - Second number
 * @returns {number} The minimum value
 */
export function math_min(a, b) {
    return Math.min(parseFloat(a || 0), parseFloat(b || 0));
};

/**
 * Get the maximum of two numbers
 * @param {number|string} a - First number
 * @param {number|string} b - Second number
 * @returns {number} The maximum value
 */
export function math_max(a, b) {
    return Math.max(parseFloat(a || 0), parseFloat(b || 0));
};

/**
 * Clamp a value between min and max
 * @param {number|string} value - The value to clamp
 * @param {number|string} min - Minimum value
 * @param {number|string} max - Maximum value
 * @returns {number} The clamped value
 */
export function math_clamp(value, min, max) {
    const v = parseFloat(value || 0);
    const minVal = parseFloat(min || 0);
    const maxVal = parseFloat(max || 0);
    return Math.min(Math.max(v, minVal), maxVal);
};
